import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Star, ThumbsUp, Send, Loader2, User, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';
import { toast } from 'sonner';

function StarRating({ rating, onRate, size = 'md', interactive = false }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-7 h-7' };
  const s = sizes[size];
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          onClick={() => interactive && onRate?.(i)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          data-testid={`star-${i}`}
        >
          <Star
            className={`${s} transition-colors ${
              i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
            } ${interactive ? 'hover:text-yellow-300' : ''}`}
          />
        </button>
      ))}
    </div>
  );
}

function RatingBar({ count, total, stars }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-8 text-right text-muted-foreground">{stars}</span>
      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
      <div className="flex-1 h-2 bg-secondary overflow-hidden">
        <div className="h-full bg-yellow-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-6 text-muted-foreground">{count}</span>
    </div>
  );
}

export function ProductReviews({ productId }) {
  const { user, token, authHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [count, setCount] = useState(0);
  const [avg, setAvg] = useState(0);
  const [dist, setDist] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);

  // Form state
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [pros, setPros] = useState('');
  const [cons, setCons] = useState('');

  useEffect(() => { fetchReviews(); }, [productId]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(res.data.reviews);
      setCount(res.data.count);
      setAvg(res.data.avg_rating);
      setDist(res.data.distribution);
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error('Wybierz ocenę (gwiazdki)'); return; }
    if (!text.trim()) { toast.error('Napisz opinię'); return; }
    if (!token) { toast.error('Zaloguj się aby dodać opinię'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/products/${productId}/reviews`, {
        rating, title, text, pros, cons
      }, { headers: authHeaders() });
      toast.success('Opinia dodana! +5 Zorków');
      setShowForm(false);
      setRating(0); setTitle(''); setText(''); setPros(''); setCons('');
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd');
    }
    setSending(false);
  };

  const markHelpful = async (reviewId) => {
    if (!token) { toast.error('Zaloguj się'); return; }
    try {
      await axios.post(`${API}/products/reviews/${reviewId}/helpful`, null, { headers: authHeaders() });
      fetchReviews();
    } catch {}
  };

  const alreadyReviewed = reviews.some(r => r.user_id === user?.id);

  return (
    <div className="mt-8" data-testid="product-reviews">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            Opinie klientów ({count})
          </h2>
          {count > 0 && (
            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-2">
                <span className="font-heading text-3xl font-bold text-yellow-400">{avg}</span>
                <div>
                  <StarRating rating={Math.round(avg)} size="sm" />
                  <p className="text-xs text-muted-foreground mt-0.5">{count} {count === 1 ? 'opinia' : count < 5 ? 'opinie' : 'opinii'}</p>
                </div>
              </div>
              <div className="flex-1 max-w-[200px] space-y-0.5">
                {[5, 4, 3, 2, 1].map(s => (
                  <RatingBar key={s} stars={s} count={dist[s] || 0} total={count} />
                ))}
              </div>
            </div>
          )}
        </div>
        {token && !alreadyReviewed && (
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-primary text-primary-foreground rounded-none font-heading uppercase tracking-wider text-xs"
            data-testid="write-review-btn"
          >
            {showForm ? <><ChevronUp className="w-4 h-4 mr-1" /> Ukryj</> : <><Star className="w-4 h-4 mr-1" /> Napisz opinię</>}
          </Button>
        )}
        {!token && (
          <Link to="/auth" className="text-xs text-primary hover:underline">Zaloguj się aby dodać opinię</Link>
        )}
      </div>

      {/* Review form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-card border border-border/40 p-5 mb-6 space-y-4" data-testid="review-form">
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Twoja ocena *</Label>
            <StarRating rating={rating} onRate={setRating} size="lg" interactive />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Tytuł opinii</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Np. Świetny produkt!" className="bg-input border-transparent focus:border-primary rounded-none" data-testid="review-title" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Twoja opinia *</Label>
            <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Opisz swoje wrażenia z produktu..." rows={4} className="bg-input border-transparent focus:border-primary rounded-none resize-none" data-testid="review-text" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-green-400 mb-1 block">Zalety</Label>
              <Textarea value={pros} onChange={e => setPros(e.target.value)} placeholder="Co Ci się podoba?" rows={2} className="bg-input border-transparent focus:border-green-500/50 rounded-none resize-none text-sm" data-testid="review-pros" />
            </div>
            <div>
              <Label className="text-sm text-red-400 mb-1 block">Wady</Label>
              <Textarea value={cons} onChange={e => setCons(e.target.value)} placeholder="Co można poprawić?" rows={2} className="bg-input border-transparent focus:border-red-500/50 rounded-none resize-none text-sm" data-testid="review-cons" />
            </div>
          </div>
          <Button type="submit" disabled={sending} className="bg-primary text-primary-foreground rounded-none font-heading uppercase tracking-wider text-sm w-full h-11" data-testid="submit-review-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Dodaj opinię (+5 Zorków)
          </Button>
        </form>
      )}

      {/* Reviews list */}
      <div className="space-y-4" data-testid="reviews-list">
        {reviews.map(r => (
          <div key={r.id} className="bg-card border border-border/40 p-4" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                  <User className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <span className="text-sm font-medium text-white">{r.user_nick}</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <StarRating rating={r.rating} size="sm" />
                    <span className="text-xs text-muted-foreground">
                      {r.created_at ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale: pl }) : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {r.title && <p className="font-medium text-white text-sm mb-1">{r.title}</p>}
            <p className="text-sm text-foreground/80 mb-3">{r.text}</p>

            {(r.pros || r.cons) && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                {r.pros && (
                  <div className="text-xs">
                    <span className="text-green-400 font-medium block mb-0.5">Zalety:</span>
                    <p className="text-muted-foreground">{r.pros}</p>
                  </div>
                )}
                {r.cons && (
                  <div className="text-xs">
                    <span className="text-red-400 font-medium block mb-0.5">Wady:</span>
                    <p className="text-muted-foreground">{r.cons}</p>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => markHelpful(r.id)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white transition-colors"
              data-testid={`helpful-${r.id}`}
            >
              <ThumbsUp className="w-3.5 h-3.5" />
              <span>Pomocna ({r.helpful || 0})</span>
            </button>
          </div>
        ))}

        {reviews.length === 0 && (
          <div className="text-center py-8 bg-card border border-border/40">
            <Star className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">Brak opinii. Bądź pierwszym który oceni ten produkt!</p>
          </div>
        )}
      </div>
    </div>
  );
}
